// ============================================================================
// Real Chat Service Adapter
// ============================================================================
// Connects to the backend AI chat API using fetch + ReadableStream for SSE.
// ============================================================================

import { useAuthStore } from "@/stores/auth-store";
import { normalizeChatRoute } from "../chat/navigation-paths";
import type {
  ChatMessage,
  ChatRequest,
  ChatResponse,
  ChatServiceAdapter,
  Citation,
  ContextPayload,
  NavigationContent,
  StepContent,
  SuggestionContent,
  TerminalContent,
  StreamingChatResponse,
  SuggestedChip,
} from "@/lib/api/chat";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1/chat";

// ---------------------------------------------------------------------------
// Auth helpers (follows client.ts pattern)
// ---------------------------------------------------------------------------

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// ---------------------------------------------------------------------------
// Message ID generation
// ---------------------------------------------------------------------------

let messageIdCounter = 0;

function nextMessageId(): string {
  messageIdCounter += 1;
  return `real-msg-${String(messageIdCounter)}-${String(Date.now())}`;
}

// ---------------------------------------------------------------------------
// Tool Call → ChatMessage Metadata Mapping
// ---------------------------------------------------------------------------

interface ToolCallResult {
  name: string;
  arguments: Record<string, unknown>;
}

/** Pre-mapped message from the backend's mapFunctionCallToMessage() */
interface FunctionMessage {
  type: "text" | "steps" | "navigation" | "suggestion" | "terminal";
  content: string;
  metadata?: Record<string, unknown> | undefined;
}

/**
 * Build a ChatMessage from the backend's raw response content and tool calls.
 * Maps tool calls to structured message types (navigation, suggestion, steps).
 * Also processes functionMessages returned by the backend (pre-mapped tool calls).
 *
 * When multiple functionMessages exist, the first matching structured type
 * becomes the primary message; any additional suggestion-type messages are
 * extracted as `suggestedReplies` on the returned ChatMessage.
 */
function buildMessageFromResponse(
  content: string,
  toolCalls?: ToolCallResult[],
  functionMessages?: FunctionMessage[],
  citations?: Citation[],
): ChatMessage {
  const id = nextMessageId();
  const base = {
    id,
    role: "assistant" as const,
    timestamp: Date.now(),
    ...(citations && citations.length > 0 ? { citations } : {}),
  };

  /**
   * Extract suggestion chips from secondary functionMessages.
   * Returns undefined when there are no additional suggestion-type messages.
   */
  const extractSecondaryChips = (
    msgs: FunctionMessage[],
    skipIndex: number,
  ): SuggestedChip[] | undefined => {
    const chips: SuggestedChip[] = [];
    for (let i = 0; i < msgs.length; i++) {
      if (i === skipIndex) continue;
      const fm = msgs[i];
      if (fm?.type === "suggestion") {
        const meta = fm.metadata as SuggestionContent | undefined;
        if (meta?.chips) {
          chips.push(...meta.chips);
        }
      }
    }
    return chips.length > 0 ? chips : undefined;
  };

  // Prefer pre-mapped functionMessages from the backend when available
  if (functionMessages && functionMessages.length > 0) {
    for (let idx = 0; idx < functionMessages.length; idx++) {
      const primary = functionMessages[idx];
      if (!primary) continue;
      const secondaryChips = extractSecondaryChips(functionMessages, idx);

      switch (primary.type) {
      case "navigation": {
        const meta = primary.metadata as NavigationContent | undefined;
        if (meta) {
          const normalizedMeta: NavigationContent = {
            ...meta,
            href: normalizeChatRoute(meta.href),
          };
          return {
            ...base,
            content: primary.content || content,
            type: "navigation",
            metadata: normalizedMeta,
            ...(secondaryChips ? { suggestedReplies: secondaryChips } : {}),
          };
        }
        break;
      }
      case "steps": {
        const meta = primary.metadata as StepContent | undefined;
        if (meta) {
          return {
            ...base,
            content: primary.content || content,
            type: "steps",
            metadata: meta,
            ...(secondaryChips ? { suggestedReplies: secondaryChips } : {}),
          };
        }
        break;
      }
      case "suggestion": {
        const meta = primary.metadata as SuggestionContent | undefined;
        if (meta) {
          return {
            ...base,
            content: primary.content || content,
            type: "suggestion",
            metadata: meta,
          };
        }
        break;
      }
      case "terminal": {
        const meta = primary.metadata as TerminalContent | undefined;
        if (meta?.scenarioId) {
          return {
            ...base,
            content: primary.content || content,
            type: "terminal",
            metadata: meta,
            ...(secondaryChips ? { suggestedReplies: secondaryChips } : {}),
          };
        }
        break;
      }
      default:
        // Skip unrecognized type; try next functionMessage
        break;
      }
    }
  }

  if (toolCalls && toolCalls.length > 0) {
    // Navigate tool → navigation message
    const navCall = toolCalls.find((tc) => tc.name === "navigate_user" || tc.name === "navigate");
    if (navCall) {
      const page = typeof navCall.arguments.page === "string"
        ? navCall.arguments.page
        : typeof navCall.arguments.href === "string"
          ? navCall.arguments.href
          : "/";
      const text = typeof navCall.arguments.text === "string"
        ? navCall.arguments.text
        : content;
      const metadata: NavigationContent = {
        type: "navigation",
        text,
        href: normalizeChatRoute(page),
      };
      if (typeof navCall.arguments.highlight_id === "string") {
        metadata.highlightOnArrival = navCall.arguments.highlight_id;
      }
      if (typeof navCall.arguments.highlightOnArrival === "string") {
        metadata.highlightOnArrival = navCall.arguments.highlightOnArrival;
      }
      return { ...base, content, type: "navigation", metadata };
    }

    // show_guided_steps tool → steps message
    const stepsCall = toolCalls.find((tc) => tc.name === "show_guided_steps");
    if (stepsCall) {
      const rawSteps = Array.isArray(stepsCall.arguments.steps)
        ? stepsCall.arguments.steps
        : [];
      const presentation =
        stepsCall.arguments.presentation === "tour" ||
        stepsCall.arguments.presentation === "guide"
          ? stepsCall.arguments.presentation
          : undefined;

      const steps = (rawSteps as Record<string, unknown>[])
        .filter(
          (s): s is Record<string, unknown> =>
            typeof s.text === "string"
        )
        .map((s) => {
          const step: { text: string; highlightTarget?: string } = {
            text: s.text as string,
          };
          if (typeof s.highlight_target === "string") {
            step.highlightTarget = s.highlight_target;
          }
          if (typeof s.highlightTarget === "string") {
            step.highlightTarget = s.highlightTarget;
          }
          return step;
        });

      if (steps.length > 0) {
        const metadata: StepContent = {
          type: "steps",
          steps,
          ...(presentation ? { presentation } : {}),
        };
        return { ...base, content, type: "steps", metadata };
      }
    }

    // suggest_actions tool → suggestion message
    const suggestCall = toolCalls.find((tc) => tc.name === "suggest_actions");
    if (suggestCall) {
      const rawActions = Array.isArray(suggestCall.arguments.actions)
        ? (suggestCall.arguments.actions as {
            label: string;
            value: string;
            icon?: string | undefined;
          }[])
        : Array.isArray(suggestCall.arguments.chips)
          ? (suggestCall.arguments.chips as {
              label: string;
              value: string;
              icon?: string | undefined;
            }[])
          : [];

      const chips: SuggestedChip[] = rawActions.map((a) => {
        const chip: SuggestedChip = { label: a.label, value: a.value };
        if (a.icon) {
          chip.icon = a.icon;
        }
        return chip;
      });

      return {
        ...base,
        content,
        type: "suggestion",
        metadata: { type: "suggestion", chips },
      };
    }

    // show_terminal_walkthrough tool → terminal message
    const terminalCall = toolCalls.find((tc) => tc.name === "show_terminal_walkthrough");
    if (terminalCall) {
      const scenarioId = typeof terminalCall.arguments.scenario_id === "string"
        ? terminalCall.arguments.scenario_id
        : undefined;

      if (scenarioId) {
        const metadata: TerminalContent = {
          type: "terminal",
          scenarioId,
        };
        return { ...base, content, type: "terminal", metadata };
      }
    }
  }

  return { ...base, content, type: "text" };
}

// ---------------------------------------------------------------------------
// SSE Event Parsing
// ---------------------------------------------------------------------------

interface SSECallbacks {
  onToken: (token: string) => void;
  onComplete: (message: ChatMessage) => void;
  onError: (error: Error) => void;
}

/**
 * Parse an SSE stream from a ReadableStream reader.
 * Events: token, tool_call, citations, done, error.
 * Handles partial line buffering (lines delimited by \n\n).
 */
async function parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: SSECallbacks,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulatedCitations: Citation[] = [];

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events (delimited by \n\n)
      const events = buffer.split("\n\n");
      // Keep the last (potentially incomplete) chunk in the buffer
      buffer = events.pop() ?? "";

      for (const eventBlock of events) {
        if (!eventBlock.trim()) continue;

        let eventType = "";
        let dataStr = "";

        for (const line of eventBlock.split("\n")) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            dataStr = line.slice(6);
          }
        }

        if (!eventType || !dataStr) continue;

        let data: unknown;
        try {
          data = JSON.parse(dataStr) as unknown;
        } catch {
          // Skip malformed JSON — don't crash the stream
          continue;
        }

        switch (eventType) {
          case "token": {
            const tokenData = data as { delta: string; index: number };
            if (tokenData.delta) {
              callbacks.onToken(tokenData.delta);
            }
            break;
          }

          case "tool_call": {
            // Tool call fragments are accumulated server-side; we handle
            // the final parsed tool calls in the "done" event.
            break;
          }

          case "citations": {
            // Citations sent before "done" for early rendering
            const citationData = data as { citations: Citation[] };
            if (Array.isArray(citationData.citations)) {
              accumulatedCitations = citationData.citations;
            }
            break;
          }

          case "done": {
            const doneData = data as {
              message: {
                content: string;
                finishReason: string;
                toolCalls?: ToolCallResult[];
                functionMessages?: FunctionMessage[];
                citations?: Citation[];
              };
            };
            // Merge citations: prefer dedicated event, fall back to done payload
            const citations =
              accumulatedCitations.length > 0
                ? accumulatedCitations
                : doneData.message.citations;
            const msg = buildMessageFromResponse(
              doneData.message.content,
              doneData.message.toolCalls,
              doneData.message.functionMessages,
              citations,
            );
            callbacks.onComplete(msg);
            break;
          }

          case "error": {
            const errorData = data as {
              code: string;
              message: string;
              statusCode?: number;
            };
            callbacks.onError(
              new Error(`[${errorData.code}] ${errorData.message}`),
            );
            break;
          }

          default:
            // Ignore unknown event types gracefully
            break;
        }
      }
    }
  } catch (err: unknown) {
    // AbortError is expected when the user cancels — not a real error
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    callbacks.onError(
      err instanceof Error ? err : new Error("SSE stream read error"),
    );
  }
}

// ---------------------------------------------------------------------------
// Error extraction helper
// ---------------------------------------------------------------------------

async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const errObj = (body as { error: { message?: string } }).error;
      if (errObj.message) return errObj.message;
    }
  } catch {
    // body wasn't JSON — fall through
  }
  return `API Error: ${String(res.status)}`;
}

// ---------------------------------------------------------------------------
// Real Chat Service Adapter
// ---------------------------------------------------------------------------

class RealChatServiceAdapter implements ChatServiceAdapter {
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const res = await fetch(`${API_BASE}/messages`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message: request.message,
        context: request.context,
        conversationHistory: request.conversationHistory,
        stream: false,
      }),
    });

    if (!res.ok) {
      throw new Error(await extractErrorMessage(res));
    }

    const json = (await res.json()) as {
      data: {
        content: string;
        finishReason: string;
        toolCalls?: ToolCallResult[];
        functionMessages?: FunctionMessage[];
        citations?: Citation[];
      };
    };

    const message = buildMessageFromResponse(
      json.data.content,
      json.data.toolCalls,
      json.data.functionMessages,
      json.data.citations,
    );

    const response: ChatResponse = { message };

    // Surface co-located suggestion chips at the response level
    // so ChatProvider can update pending suggestions
    if (message.suggestedReplies) {
      response.suggestedReplies = message.suggestedReplies;
    }

    return response;
  }

  sendMessageStreaming(
    request: ChatRequest,
    callbacks: Omit<StreamingChatResponse, "abort">,
  ): StreamingChatResponse {
    const controller = new AbortController();

    const run = async () => {
      try {
        const res = await fetch(`${API_BASE}/messages`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            message: request.message,
            context: request.context,
            conversationHistory: request.conversationHistory,
            stream: true,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          callbacks.onError(new Error(await extractErrorMessage(res)));
          return;
        }

        if (!res.body) {
          callbacks.onError(
            new Error("Response body is null — streaming not supported"),
          );
          return;
        }

        const reader = res.body.getReader();
        await parseSSEStream(reader, callbacks);
      } catch (err: unknown) {
        // AbortError is expected when the user cancels
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        callbacks.onError(
          err instanceof Error ? err : new Error("Streaming request failed"),
        );
      }
    };

    // Fire-and-forget — caller controls via abort()
    void run();

    return {
      onToken: callbacks.onToken,
      onComplete: callbacks.onComplete,
      onError: callbacks.onError,
      abort: () => {
        controller.abort();
      },
    };
  }

  async getProactiveSuggestions(
    context: ContextPayload,
  ): Promise<SuggestedChip[]> {
    try {
      const res = await fetch(`${API_BASE}/suggestions`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ context }),
      });

      if (!res.ok) {
        // Suggestions are non-critical — fail silently with empty array
        return [];
      }

      const json = (await res.json()) as { data: SuggestedChip[] };
      return json.data;
    } catch {
      // Network errors for suggestions are non-critical
      return [];
    }
  }
}

// ---------------------------------------------------------------------------
// Adapter Factory
// ---------------------------------------------------------------------------

/**
 * Create the real chat adapter used by the web app.
 */
export function getChatAdapter(): ChatServiceAdapter {
  return new RealChatServiceAdapter();
}

/** Export class for testing purposes */
export function createRealChatAdapter(): ChatServiceAdapter {
  return new RealChatServiceAdapter();
}
